import { Flex } from 'antd';
import { Tag, TagColors } from '@packages/utils';
import { TMenuProps } from '@idaho-aeyc/ui-kit';
import { ButtonDropdown } from 'components/Base/ButtonDropdown';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import { useUpdateWorkshopTaskStatusMutation } from 'redux/apiSlice/workshopTasks.slice';
import { EWorkshopTaskStatus } from 'data/types/workshopTasks.types';
import { ChevronDown } from 'assets/icon/icon';

const STATUS_COLOR: Record<EWorkshopTaskStatus, TagColors> = {
  [EWorkshopTaskStatus.TO_DO]: 'grey',
  [EWorkshopTaskStatus.IN_PROGRESS]: 'primary',
  [EWorkshopTaskStatus.STALLED]: 'danger',
  [EWorkshopTaskStatus.COMPLETED]: 'success',
};

const STATUS_LABEL: Record<EWorkshopTaskStatus, string> = {
  [EWorkshopTaskStatus.TO_DO]: 'To Do',
  [EWorkshopTaskStatus.IN_PROGRESS]: 'In Progress',
  [EWorkshopTaskStatus.STALLED]: 'Stalled',
  [EWorkshopTaskStatus.COMPLETED]: 'Completed',
};

const STATUS_OPTIONS: EWorkshopTaskStatus[] = [
  EWorkshopTaskStatus.TO_DO,
  EWorkshopTaskStatus.IN_PROGRESS,
  EWorkshopTaskStatus.STALLED,
  EWorkshopTaskStatus.COMPLETED,
];

type WorkshopTaskStatusCellProps = {
  workshopId: string;
  taskId: string;
  value: EWorkshopTaskStatus;
  disabled?: boolean;
};

const TAG_STYLE: React.CSSProperties = {
  width: 95,
  textAlign: 'center',
  marginRight: 0,
};

const WorkshopTaskStatusCell = ({
  workshopId,
  taskId,
  value,
  disabled,
}: WorkshopTaskStatusCellProps) => {
  const [updateStatus, { isLoading }] = useUpdateWorkshopTaskStatusMutation();
  const { showToastNotification } = useShowToastNotification();

  const inFlight = isLoading;
  const cannotEdit = disabled || inFlight;

  const handleSelect = (next: EWorkshopTaskStatus) => async () => {
    if (next === value) return;
    try {
      await updateStatus({ workshopId, taskId, status: next }).unwrap();
    } catch {
      showToastNotification({
        message: 'Could not update task status. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const items: TMenuProps['items'] = STATUS_OPTIONS.map(option => ({
    label: (
      <Tag
        color={STATUS_COLOR[option]}
        data-cy="task-status-tag"
        style={TAG_STYLE}
      >
        {STATUS_LABEL[option]}
      </Tag>
    ),
    key: option,
    onClick: handleSelect(option),
    disabled: cannotEdit,
  }));

  return (
    <Flex
      gap={8}
      align="center"
      data-cy="task-status-dropdown"
      data-testid="task-status"
    >
      <Tag
        color={STATUS_COLOR[value]}
        data-cy="task-status-tag"
        style={TAG_STYLE}
      >
        {STATUS_LABEL[value]}
      </Tag>
      <ButtonDropdown
        buttonProps={{
          children: <ChevronDown size={16} />,
          color: 'primary',
          style: {
            border: '0',
            background: 'none',
            padding: 0,
          },
          disabled: cannotEdit,
        }}
        dropdownProps={{
          menu: { items },
          rootClassName: 'button-dropdown',
          placement: 'bottomRight',
          disabled: cannotEdit,
        }}
      />
    </Flex>
  );
};

export default WorkshopTaskStatusCell;
